export const EXAMPLE_API_KEY = 'go_0a4c85f9e12b3d6a789c01234def5678';

export const buildCurlExample = (apiUrl: string): string => `curl -X POST "${apiUrl}/v1/signals/" \\
  -H "Authorization: Bearer ${EXAMPLE_API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "John Smith",
    "customer_email": "john.smith@example.com",
    "customer_phone": "+44 7700 900000",
    "customer_id": "CUST-123456",
    "verbatim": "I've been trying to close my account for 3 months. Every time I call, I'm told it will be done within 48 hours, but nothing happens. This is causing me significant stress as I'm still being charged monthly fees.",
    "channel": "email",
    "product_policy": "Premium Account",
    "journey_stage": "account_closure",
    "source": "Customer Support Email",
    "metadata": {
      "ticket_id": "TICKET-789012",
      "priority": "high",
      "category": "account_management"
    }
  }'`;

export const buildPythonExample = (apiUrl: string): string => `import requests
import json
from datetime import datetime

url = "${apiUrl}/v1/signals/"
headers = {
    "Authorization": "Bearer ${EXAMPLE_API_KEY}",
    "Content-Type": "application/json"
}

email_signal = {
    "customer_name": "Sarah Johnson",
    "customer_email": "sarah.j@example.com",
    "customer_phone": "+44 7700 900123",
    "customer_id": "CUST-789012",
    "verbatim": "I've been trying to close my account for 3 months. Every time I call, I'm told it will be done within 48 hours, but nothing happens. This is causing me significant stress as I'm still being charged monthly fees.",
    "channel": "email",
    "product_policy": "Premium Account",
    "journey_stage": "account_closure",
    "source": "Customer Support Email",
    "metadata": {
        "ticket_id": "TICKET-789012",
        "priority": "high",
        "category": "account_management",
        "email_subject": "Urgent: Account Closure Request - 3rd Attempt"
    }
}

response = requests.post(url, json=email_signal, headers=headers)
if response.status_code == 201:
    print(f"Signal created successfully: {response.json()}")
else:
    print(f"Error: {response.status_code} - {response.text}")`;

export const buildJavaExample = (apiUrl: string): string => `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import java.util.HashMap;
import java.util.Map;
import com.fasterxml.jackson.databind.ObjectMapper;

public class SignalSubmission {
    private static final String API_URL = "${apiUrl}/v1/signals/";
    private static final String API_KEY = "${EXAMPLE_API_KEY}";

    public static void main(String[] args) throws Exception {
        HttpClient client = HttpClient.newHttpClient();
        ObjectMapper mapper = new ObjectMapper();

        Map<String, Object> surveySignal = new HashMap<>();
        surveySignal.put("customer_name", "David Chen");
        surveySignal.put("customer_email", "d.chen@example.com");
        surveySignal.put("customer_id", "CUST-445566");
        surveySignal.put("verbatim", "Very disappointed with the claims process. Took 3 months to resolve a simple claim. Had to chase multiple times. Would not recommend to others.");
        surveySignal.put("channel", "survey");
        surveySignal.put("product_policy", "Motor Insurance");
        surveySignal.put("journey_stage", "post_claim");
        surveySignal.put("source", "NPS Survey");

        Map<String, Object> surveyMetadata = new HashMap<>();
        surveyMetadata.put("nps_score", 3);
        surveyMetadata.put("survey_id", "SURVEY-2024-0145");
        surveyMetadata.put("claim_reference", "MTR-2023-9876");
        surveySignal.put("metadata", surveyMetadata);

        String jsonBody = mapper.writeValueAsString(surveySignal);

        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(API_URL))
            .header("Authorization", "Bearer " + API_KEY)
            .header("Content-Type", "application/json")
            .POST(HttpRequest.BodyPublishers.ofString(jsonBody))
            .build();

        HttpResponse<String> response = client.send(request, HttpResponse.BodyHandlers.ofString());

        if (response.statusCode() == 201) {
            System.out.println("Signal created successfully!");
            Map<String, Object> result = mapper.readValue(response.body(), Map.class);
            System.out.println("Signal ID: " + result.get("signal_id"));
            System.out.println("Source ID: " + result.get("source_id"));
        } else {
            System.err.println("Error creating signal:");
            System.err.println("Status: " + response.statusCode());
            System.err.println("Response: " + response.body());
        }
    }
}`;

export const buildSocialThreadExample = (apiUrl: string): string => `curl -X POST "${apiUrl}/v1/signals/ingest" \\
  -H "Authorization: Bearer ${EXAMPLE_API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "raw_text": "Payment failed - Cannot complete payment on website",
    "channel_type": "social_media",
    "customer_name": "Sarah Johnson",
    "occurred_at": "2025-01-01T10:00:00Z",
    "external_thread_id": "twitter-conv-12345",
    "thread_messages": [{
      "external_id": "tweet-001",
      "author_type": "customer",
      "author_name": "Sarah Johnson",
      "content": "@YourCompany Cannot complete payment on website. Getting error 500. Help!",
      "sent_at": "2025-01-01T10:00:00Z",
      "message_type": "customer_message"
    }],
    "channel_metadata": {
      "platform": "twitter",
      "tweet_url": "https://twitter.com/user/status/12345",
      "mentions": ["@YourCompany"]
    }
  }'`;

export const buildChatIngestExample = (apiUrl: string): string => `import requests
from datetime import datetime

API_URL = "${apiUrl}/v1/signals/ingest"
API_KEY = "${EXAMPLE_API_KEY}"

def send_chat_message(thread_id, message_id, author_type, author_name, content):
    payload = {
        "raw_text": f"Chat: {content[:50]}...",
        "channel_type": "chat",
        "customer_name": author_name if author_type == "customer" else "Customer",
        "occurred_at": datetime.utcnow().isoformat() + "Z",
        "external_thread_id": thread_id,
        "thread_messages": [{
            "external_id": message_id,
            "author_type": author_type,
            "author_name": author_name,
            "content": content,
            "sent_at": datetime.utcnow().isoformat() + "Z",
            "message_type": "customer_message" if author_type == "customer" else "agent_response"
        }]
    }

    response = requests.post(
        API_URL,
        json=payload,
        headers={"Authorization": f"Bearer {API_KEY}"}
    )

    return response.json()

chat_thread_id = "chat-session-98765"
send_chat_message(chat_thread_id, "msg-001", "customer", "John Smith", "Hello, I'm having trouble with my recent order #12345")
send_chat_message(chat_thread_id, "msg-002", "agent", "Agent Maria", "Hello John, I'd be happy to help. Let me look up your order.")
send_chat_message(chat_thread_id, "msg-003", "customer", "John Smith", "I was charged twice for the same item. This is really frustrating!")`;
